<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { useRoute } from 'vue-router';
import {
  useStoreGetters,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';
// NOTICE: KAPTION-INTEGRATION
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
// NOTICE: KAPTION-INTEGRATION — feature permission gate for broadcasts
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import WhatsAppCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignDialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import WhatsAppCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/WhatsAppCampaignEmptyState.vue';
// NOTICE: KAPTION-INTEGRATION - Campaigns/Templates tabs
import WhatsAppCampaignTabs from './WhatsAppCampaignTabs.vue';

const { t } = useI18n();
const route = useRoute();
const getters = useStoreGetters();
const store = useStore();

const selectedCampaign = ref(null);
const [showWhatsAppCampaignDialog, toggleWhatsAppCampaignDialog] = useToggle();

// NOTICE: KAPTION-INTEGRATION - Route to full-page builder
const newCampaignRoute = computed(() => ({
  name: 'campaigns_whatsapp_new',
  params: { accountId: route.params.accountId },
}));

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

// NOTICE: KAPTION-INTEGRATION
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
const showEnhancedBuilder = computed(() =>
  isKaptionFlagEnabled('KAPTION_ENHANCED_CAMPAIGN_BUILDER')
);
// NOTICE: KAPTION-INTEGRATION — gate broadcast campaign creation
const { canUseFeature } = useFeaturePermissions();
const canCreateBroadcasts = computed(() =>
  canUseFeature('campaign_broadcasts')
);

// NOTICE: KAPTION-INTEGRATION
// Pagination and sorting state
const pagination = useMapGetter('campaigns/getPagination');
const sorting = useMapGetter('campaigns/getSorting');

// NOTICE: KAPTION-INTEGRATION - Archive filter
const archiveFilter = useMapGetter('campaigns/getArchiveFilter');

const confirmDeleteCampaignDialogRef = ref(null);

const WhatsAppCampaigns = computed(
  () => getters['campaigns/getWhatsAppCampaigns'].value
);

const hasNoWhatsAppCampaigns = computed(
  () => WhatsAppCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

// NOTICE: KAPTION-INTEGRATION
// Paginated campaigns (show only current page)
const paginatedCampaigns = computed(() => {
  const campaigns = WhatsAppCampaigns.value || [];
  const currentPage = pagination.value?.currentPage || 1;
  const perPage = pagination.value?.perPage || 10;
  const startIndex = (currentPage - 1) * perPage;
  const endIndex = startIndex + perPage;
  return campaigns.slice(startIndex, endIndex);
});

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};

// NOTICE: KAPTION-INTEGRATION
// Pagination handlers
const handlePageChange = async page => {
  await store.dispatch('campaigns/fetchPage', {
    page,
    perPage: pagination.value?.perPage || 10,
    sortBy: sorting.value?.sortBy || 'created_at',
    sortOrder: sorting.value?.sortOrder || 'desc',
  });
};

const handleSortChange = async (sortBy, sortOrder) => {
  await store.dispatch('campaigns/updateSorting', { sortBy, sortOrder });
  await store.dispatch('campaigns/fetchPage', {
    page: 1,
    perPage: pagination.value?.perPage || 10,
    sortBy,
    sortOrder,
  });
};

// NOTICE: KAPTION-INTEGRATION - Archive/Unarchive handlers
const handleArchive = async campaign => {
  try {
    await store.dispatch('campaigns/archive', campaign.id);
  } catch (error) {
    // Error handled by store
  }
};

const handleUnarchive = async campaign => {
  try {
    await store.dispatch('campaigns/unarchive', campaign.id);
  } catch (error) {
    // Error handled by store
  }
};

const handleArchiveFilterChange = async filter => {
  await store.dispatch('campaigns/setArchiveFilter', filter);
  await store.dispatch('campaigns/fetchPage', {
    page: 1,
    perPage: pagination.value?.perPage || 10,
    sortBy: sorting.value?.sortBy || 'created_at',
    sortOrder: sorting.value?.sortOrder || 'desc',
    archiveFilter: filter,
  });
};

// Initialize: fetch campaigns with pagination
onMounted(async () => {
  await store.dispatch('campaigns/get', {
    page: 1,
    perPage: 10,
    sortBy: 'created_at',
    sortOrder: 'desc',
  });
});
</script>

<template>
  <CampaignLayout :header-title="t('CAMPAIGN.WHATSAPP.HEADER_TITLE')">
    <!-- NOTICE: KAPTION-INTEGRATION Custom header actions -->
    <!-- NOTICE: KAPTION-INTEGRATION — hide "New Campaign" when broadcasts are off -->
    <template v-if="canCreateBroadcasts" #header-action>
      <div class="flex gap-2">
        <Button
          v-if="!showEnhancedBuilder"
          :label="t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN')"
          @click="toggleWhatsAppCampaignDialog()"
        />
        <!-- NOTICE: KAPTION-INTEGRATION - Link to full-page builder -->
        <router-link
          v-if="showEnhancedBuilder"
          :to="newCampaignRoute"
          class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-n-brand hover:bg-n-brand-hover rounded-lg transition-colors shadow-sm"
        >
          <span class="i-lucide-plus w-4 h-4" />
          {{ t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN') }}
        </router-link>
      </div>
    </template>

    <template #action>
      <WhatsAppCampaignDialog
        v-if="showWhatsAppCampaignDialog && canCreateBroadcasts"
        @close="toggleWhatsAppCampaignDialog(false)"
      />
    </template>
    <!-- NOTICE: KAPTION-INTEGRATION - Campaigns/Templates tabs + archive filter (same row) -->
    <div class="flex items-center justify-between mb-4">
      <WhatsAppCampaignTabs />
      <!-- NOTICE: KAPTION-INTEGRATION - Archive filter toggle (right-aligned) -->
      <div class="flex items-center gap-2">
        <button
          v-for="filter in ['active', 'archived', 'all']"
          :key="filter"
          class="px-3 py-1 text-sm rounded-md transition-colors"
          :class="[
            archiveFilter === filter
              ? 'bg-n-brand text-white'
              : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3',
          ]"
          @click="handleArchiveFilterChange(filter)"
        >
          {{ t(`CAMPAIGN.ARCHIVE.FILTER.${filter.toUpperCase()}`) }}
        </button>
      </div>
    </div>
    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <CampaignList
      v-else-if="!hasNoWhatsAppCampaigns"
      :campaigns="paginatedCampaigns"
      @delete="handleDelete"
      @archive="handleArchive"
      @unarchive="handleUnarchive"
    />
    <!-- NOTICE: KAPTION-INTEGRATION -->
    <!-- Pagination Controls -->
    <div
      v-if="!hasNoWhatsAppCampaigns && pagination?.totalPages > 1"
      class="flex items-center justify-between px-6 py-4 border-t border-n-container"
    >
      <div class="flex items-center gap-2">
        <span class="text-sm text-n-slate-11">
          {{
            t('CAMPAIGN.PAGINATION.SHOWING', {
              from: (pagination.currentPage - 1) * pagination.perPage + 1,
              to: Math.min(
                pagination.currentPage * pagination.perPage,
                pagination.totalCount
              ),
              total: pagination.totalCount,
            })
          }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <button
          :disabled="pagination.currentPage === 1"
          class="px-3 py-1 text-sm rounded-md"
          :class="[
            pagination.currentPage === 1
              ? 'bg-n-alpha-2 text-n-slate-9 cursor-not-allowed'
              : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3',
          ]"
          @click="handlePageChange(pagination.currentPage - 1)"
        >
          {{ t('CAMPAIGN.PAGINATION.PREVIOUS') }}
        </button>
        <span class="text-sm text-n-slate-11">
          {{
            t('CAMPAIGN.PAGINATION.PAGE', {
              current: pagination.currentPage,
              total: pagination.totalPages,
            })
          }}
        </span>
        <button
          :disabled="pagination.currentPage === pagination.totalPages"
          class="px-3 py-1 text-sm rounded-md"
          :class="[
            pagination.currentPage === pagination.totalPages
              ? 'bg-n-alpha-2 text-n-slate-9 cursor-not-allowed'
              : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3',
          ]"
          @click="handlePageChange(pagination.currentPage + 1)"
        >
          {{ t('CAMPAIGN.PAGINATION.NEXT') }}
        </button>
      </div>
    </div>
    <!-- Sorting Controls -->
    <div
      v-if="!hasNoWhatsAppCampaigns"
      class="flex items-center justify-end gap-2 px-6 py-2"
    >
      <span class="text-sm text-n-slate-11">{{ t('CAMPAIGN.SORT.BY') }}</span>
      <select
        :value="sorting?.sortBy || 'created_at'"
        class="px-3 py-1 text-sm rounded-md bg-n-alpha-2 text-n-slate-11 border border-n-container"
        @change="
          handleSortChange($event.target.value, sorting?.sortOrder || 'desc')
        "
      >
        <option value="created_at">{{ t('CAMPAIGN.SORT.CREATED_AT') }}</option>
        <option value="updated_at">{{ t('CAMPAIGN.SORT.UPDATED_AT') }}</option>
        <option value="title">{{ t('CAMPAIGN.SORT.TITLE') }}</option>
        <option value="campaign_status">{{ t('CAMPAIGN.SORT.STATUS') }}</option>
      </select>
      <button
        class="px-3 py-1 text-sm rounded-md"
        :class="[
          sorting?.sortOrder === 'desc'
            ? 'bg-n-teal-9 text-n-teal-12'
            : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3',
        ]"
        @click="
          handleSortChange(
            sorting?.sortBy || 'created_at',
            sorting?.sortOrder === 'desc' ? 'asc' : 'desc'
          )
        "
      >
        <span v-if="sorting?.sortOrder === 'desc'">{{
          t('CAMPAIGN.SORT.DESC')
        }}</span>
        <span v-else>{{ t('CAMPAIGN.SORT.ASC') }}</span>
      </button>
    </div>
    <WhatsAppCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    >
      <!-- NOTICE: KAPTION-INTEGRATION Override empty state actions -->
      <template v-if="canCreateBroadcasts" #actions>
        <div class="flex gap-2">
          <Button
            v-if="!showEnhancedBuilder"
            :label="t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN')"
            @click="toggleWhatsAppCampaignDialog()"
          />
          <!-- NOTICE: KAPTION-INTEGRATION - Link to full-page builder -->
          <router-link
            v-if="showEnhancedBuilder"
            :to="newCampaignRoute"
            class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-n-brand hover:bg-n-brand-hover rounded-lg transition-colors shadow-sm"
          >
            <span class="i-lucide-plus w-4 h-4" />
            {{ t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN') }}
          </router-link>
        </div>
      </template>
    </WhatsAppCampaignEmptyState>
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
  </CampaignLayout>
</template>
